import { useEffect, useState } from "react";
import { Loader2, Lock, Unlock } from "lucide-react";
import t from "@/lang/lang";
import { GfsOffer } from "@/services/backend/gfs/offer/types";
import { useGfsOfferSetImmutableMutation } from "@/services/backend/gfs/offer/api";
import { Button } from "@/shared/components/ui/button";
import { DialogClose, DialogFooter } from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { DialogTooltipButton } from "@/shared/components/dialog/dialog-tooltip-button";
import { toast } from "sonner";

export function SetGfsOfferImmutableButton({
  offerId,
  immutable = false,
  disabled,
  triggerLabel,
  onUpdated,
}: {
  offerId: string;
  immutable?: boolean;
  disabled?: boolean;
  triggerLabel?: string;
  onUpdated?: (offer: GfsOffer) => void;
}) {
  const [open, setOpen] = useState(false);
  const [setImmutable, { data, error, isLoading, isSuccess, reset }] =
    useGfsOfferSetImmutableMutation();

  useEffect(() => {
    if (!isSuccess || !data) {
      return;
    }
    onUpdated?.(data);
    setOpen(false);
    toast.success(t("Angebot festgeschrieben"), {
      description: t("Das Angebot kann jetzt nicht mehr bearbeitet werden."),
    });
    reset();
  }, [data, isSuccess, onUpdated, reset]);

  const stateIcon = immutable ? (
    <Lock className="h-5 w-5" />
  ) : (
    <Unlock className="h-5 w-5" />
  );

  return (
    <DialogTooltipButton
      open={open}
      onOpenChange={setOpen}
      tooltip={
        immutable
          ? t("Angebot ist festgeschrieben")
          : t("Angebot festschreiben")
      }
      dialogTitle={t("Angebot festschreiben")}
      triggerContent={
        triggerLabel ? (
          <>
            {stateIcon}
            <span>{triggerLabel}</span>
          </>
        ) : (
          stateIcon
        )
      }
      buttonProps={{
        type: "button",
        variant: "outline",
        disabled: immutable || disabled || isLoading,
        className: triggerLabel ? "w-full justify-start gap-2" : undefined,
      }}
      content={
        <>
          <p className="text-muted-foreground text-sm">
            {t(
              "Mit dem Festschreiben wird das Angebot verbindlich gespeichert und kann danach nicht mehr bearbeitet werden.",
            )}
          </p>
          <p className="text-muted-foreground mt-2 text-sm">
            {t(
              "Für Änderungen erstellen Sie bitte ein neues Angebot. Dafür können Sie ein bestehendes Angebot als Vorlage verwenden.",
            )}
          </p>
          <RTKQueryErrorAlert
            error={error}
            onDismiss={() => {
              reset();
            }}
          />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("Abbrechen")}</Button>
            </DialogClose>
            <Button
              onClick={() => {
                if (isLoading || immutable) {
                  return;
                }
                setImmutable({ id: offerId });
              }}
              disabled={isLoading || immutable}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Lock className="mr-2 h-4 w-4" />
              )}
              {t("Angebot festschreiben")}
            </Button>
          </DialogFooter>
        </>
      }
    />
  );
}
